/**
 * S-07 Onboarding — photos & prompts (V1 auth-profiles-gdpr).
 * Minimum to proceed: 1 photo in the SM-13d pipeline + 1 prompt answer.
 * 6 slots (shared PhotoManager), prompt picker (©prompts.pool), bio with
 * ©profile.bio_placeholder, anthem; vitals & interests are "later is fine"
 * (they live in S-25). A quarantined photo does NOT block continuing —
 * S-09 resolves the pending-review state (screens.md).
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { copy } from "@humble/shared";
import { Button, Card, toast } from "../../design/index.js";
import { api, isApiError } from "../../lib/api.js";
import { PhotoManager, type PhotoItem } from "../profile/photo-manager.js";
import { PromptsEditor, type PromptAnswer } from "../profile/prompts-editor.js";
import { OnboardingFrame } from "./onboarding-frame.js";
import "./onboarding.css";

interface MeSlice {
  profile?: {
    bio?: string;
    anthem?: string;
    photo_items?: Array<{ id?: string; status?: string; position?: number | null }>;
    prompts?: Array<{ prompt_id?: number; answer?: string }>;
  };
}

export function ProfileSetupScreen() {
  const navigate = useNavigate();
  const [me, setMe] = useState<MeSlice | null>(null);
  const [bio, setBio] = useState("");
  const [anthem, setAnthem] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const view = (await api.get("/me")) as MeSlice;
      setMe(view);
      setBio((prev) => prev || (view.profile?.bio ?? ""));
      setAnthem((prev) => prev || (view.profile?.anthem ?? ""));
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const photos: PhotoItem[] = (me?.profile?.photo_items ?? [])
    .filter((p): p is { id: string; status: string; position: number | null } => typeof p.id === "string")
    .map((p) => ({ id: p.id, kind: "photo", status: p.status, position: p.position ?? null }));
  const prompts: PromptAnswer[] = (me?.profile?.prompts ?? [])
    .filter((p): p is { prompt_id: number; answer: string } => typeof p.prompt_id === "number")
    .map((p) => ({ prompt_id: p.prompt_id, answer: p.answer ?? "" }));

  const hasPhoto = photos.length > 0;
  const hasPrompt = prompts.length > 0;

  async function onContinue() {
    if (busy) return;
    if (!hasPhoto) {
      setError(copy.profile.need_one_photo);
      return;
    }
    if (!hasPrompt) {
      setError(copy.profile.empty_slots.prompts);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.patch("/me/profile", { body: { bio, anthem } });
      navigate("/onboard/location");
    } catch (err) {
      setError(isApiError(err) ? err.message : "Network error — try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <OnboardingFrame step={2} screen="S-07">
      <Card padding="lg">
        <p className="h-caps onboard__kicker">Step 2 of 4</p>
        <h1 className="type-display-3 onboard__title">
          Show your face.{" "}
          <em className="type-editorial" style={{ textTransform: "none" }}>
            And your flaws.
          </em>
        </h1>

        <h2 className="me__section-title" style={{ marginTop: 10 }}>
          Photos — at least one
        </h2>
        {!hasPhoto && <p className="text-faint">{copy.profile.empty_slots.photos}</p>}
        <PhotoManager photos={photos} onChanged={() => void refresh()} />

        <h2 className="me__section-title" style={{ marginTop: 16 }}>
          Prompts — answer at least one
        </h2>
        <PromptsEditor answers={prompts} onSaved={() => void refresh()} />

        <div className="field" style={{ marginTop: 12 }}>
          <label htmlFor="onboard-bio">Bio (optional, 500 max)</label>
          <textarea
            id="onboard-bio"
            rows={3}
            maxLength={500}
            placeholder={copy.profile.bio_placeholder}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="onboard-anthem">Anthem (optional, one line)</label>
          <input
            id="onboard-anthem"
            type="text"
            maxLength={60}
            placeholder={copy.profile.anthem_placeholder}
            value={anthem}
            onChange={(e) => setAnthem(e.target.value)}
          />
        </div>

        <p className="text-faint" style={{ fontSize: "0.85rem" }}>
          Vitals, interests and the rest: later is fine — they live in your profile page.
        </p>

        {error && (
          <p className="field__error" role="alert">
            {error}
          </p>
        )}
        <div className="onboard__actions">
          <Button
            block
            variant="primary"
            size="lg"
            loading={busy}
            disabled={!hasPhoto || !hasPrompt}
            onClick={() => void onContinue()}
          >
            Continue
          </Button>
        </div>
      </Card>
    </OnboardingFrame>
  );
}
